import { useEffect } from 'react';
import { AlertTriangle, Loader2, MonitorSmartphone } from 'lucide-react';
import { Button } from '../components/Button';
import { useDbState, useTableInvalidation } from '../db/hooks';
import { openDb, takeOverDb, type DbTarget } from '../db/session';
import type { DbClient } from '../db/client';

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center px-6 text-center">{children}</div>;
}

function Ready({ client, children }: { client: DbClient; children: React.ReactNode }) {
  useTableInvalidation(client);
  return <>{children}</>;
}

/** Opens the database for `target` and renders children only once it is ready. */
export function DbGate({ target, children }: { target: DbTarget; children: React.ReactNode }) {
  const state = useDbState();
  const targetKey = target.mode === 'demo' ? 'demo' : target.userId;

  useEffect(() => {
    void openDb(target);
    // targetKey captures everything that identifies the target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  if (state.status === 'ready') return <Ready client={state.client}>{children}</Ready>;

  if (state.status === 'other-tab') {
    return (
      <Centered>
        <div className="max-w-sm space-y-4">
          <MonitorSmartphone className="mx-auto h-10 w-10 text-clinical-primary" />
          <h1 className="text-xl font-semibold">RenalFlow is open in another tab</h1>
          <p className="text-sm text-clinical-muted">Your data can only be open in one tab at a time, so nothing gets out of step.</p>
          <Button onClick={() => void takeOverDb()}>Use here</Button>
        </div>
      </Centered>
    );
  }

  if (state.status === 'unsupported' || state.status === 'error') {
    return (
      <Centered>
        <div className="max-w-sm space-y-4">
          <AlertTriangle className="mx-auto h-10 w-10 text-clinical-warning" />
          <h1 className="text-xl font-semibold">{state.status === 'unsupported' ? 'This browser isn’t supported' : 'Couldn’t open your data'}</h1>
          <p className="text-sm text-clinical-muted">{state.message}</p>
          {state.status === 'unsupported' ? (
            <p className="text-sm text-clinical-muted">Use an up-to-date Chrome, Edge, Firefox or Safari (iOS 17 or later).</p>
          ) : (
            <Button onClick={() => window.location.reload()}>Try again</Button>
          )}
        </div>
      </Centered>
    );
  }

  return (
    <Centered>
      <Loader2 className="h-8 w-8 animate-spin text-clinical-primary" aria-label="Opening your data" />
    </Centered>
  );
}
